import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Images, Loader2, Search } from 'lucide-react'

import { listAssets, type ProductionAsset } from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { assetMediaUrl, resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { ProtoBtn, ProtoInput, ProtoModal, ProtoThumb } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { cn } from '@/shared/lib/utils'

function isImageAsset(asset: ProductionAsset): boolean {
  const url = assetMediaUrl(asset)
  if (!url) return false
  if (asset.kind === 'take_video') return false
  return true
}

export function AssetMultiPickerModal({
  open,
  onClose,
  productionId,
  selectedUrls,
  maxSelect,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  selectedUrls: string[]
  maxSelect?: number
  onConfirm: (urls: string[], catalog?: ProductionAsset[]) => void
}) {
  const { t } = useTranslation()
  const [items, setItems] = useState<ProductionAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    setPicked(selectedUrls)
    setQuery('')
    setError(null)
  }, [open, selectedUrls])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listAssets(productionId)
      setItems(res.items.filter(isImageAsset))
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId])

  useEffect(() => {
    if (!open) return
    void reload()
  }, [open, reload])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.note?.toLowerCase().includes(q) ?? false) ||
        (a.tags ?? []).some((tag) => tag.toLowerCase().includes(q)),
    )
  }, [items, query])

  function toggle(url: string) {
    setPicked((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url)
      if (maxSelect === 1) return [url]
      return [...prev, url]
    })
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('bible.pickAssetsTitle')}
      size="xl"
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn
            size="sm"
            onClick={() => onConfirm(picked, items)}
          >
            <Check className="h-4 w-4" />
            {t('bible.pickAssetsConfirm', { count: picked.length })}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <ProtoInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('bible.pickAssetsSearch')}
          className="pl-9"
        />
      </div>
      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="rounded-xl border border-dashed border-white/[0.08] p-6 text-center text-sm text-zinc-500">
          {t('bible.pickAssetsEmpty')}
        </p>
      ) : (
        <div className={cn('studio-scrollbar-hidden max-h-[min(52vh,420px)] overflow-y-auto', CATALOG_LIST_GRID_CLASS)}>
          {filtered.map((asset) => {
            const url = assetMediaUrl(asset)
            const active = picked.includes(url)
            return (
              <Button
                key={asset.id}
                type="button"
                variant="outline"
                onClick={() => toggle(url)}
                className={cn(
                  'group relative h-auto overflow-hidden rounded-xl p-0 text-left font-normal',
                  active
                    ? 'border-indigo-500/50 ring-2 ring-indigo-500/30'
                    : 'border-white/[0.06] hover:border-white/15',
                )}
              >
                {url ? (
                  <img src={resolveMediaDisplayUrl(url)} alt="" className="aspect-square w-full object-cover" />
                ) : (
                  <ProtoThumb seed={asset.id} aspect="square" className="aspect-square w-full" />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="truncate text-[10px] text-zinc-200">{asset.name}</p>
                </div>
                {active ? (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white">
                    <Check className="h-3 w-3" />
                  </span>
                ) : (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/40 text-zinc-300 opacity-0 transition group-hover:opacity-100">
                    <Images className="h-3 w-3" />
                  </span>
                )}
              </Button>
            )
          })}
        </div>
      )}
    </ProtoModal>
  )
}
